"use client";

import * as X from "@/ui";

import BlogImage from "./assets/learnmore-blog-illu.png";
import AltBlogImage from "./assets/learnmore-blog-alt.png";

import SuccessIcon from "@/ui/shared/assets/newsletter-success.svg";
import WarningIcon from "@/ui/shared/assets/newsletter-warning.svg";


export function LearnMoreSection(props: X.PageProps<{
  variant?: "support" | "home";
}>) {
  const tr = X.useTranslator();
  const form = X.useNewsletterForm();

  return (
    <section className="xl:my-24 xl:mb-30">
      <div className="max-w-content mx-auto flex flex-col xl:flex-row items-stretch xl:gap-8">
        <div className={X.clsx(props.variant !== "support" && "bg-surface-purple", "flex-1 xl:rounded-4xl py-10 xl:py-10 xl:pt-8 px-mobile-margin xl:px-10")}>
          <h3 className="text-[26px] xl:text-[48px] [&_strong]:font-medium w-[85%] xl:w-full leading-tight mb-8">
            {tr({
              en: <>Get inspired on <strong>our blog</strong></>,
              fr: <>Laissez-vous inspirer par <strong>notre blog</strong></>,
            })}
          </h3>
          <div className="flex flex-row items-start xl:items-stretch gap-4 xl:gap-8">
            <div className="flex flex-col justify-between">
              <p className="text-[15px] font-light xl:font-normal leading-tight xl:text-[20px] mb-8">
                {tr({
                  en: "From shopping tips across Europe to smart VAT refund hacks, we’ve gathered the best ideas to simplify your travels (and your life). Oh, and all the latest news on tax-free shopping too.",
                  fr: "Nous rassemblons pour vous les bons plans shopping en Europe, les trucs et astuces qui vous simplifierons la vie... et bien sûr de toute l’actualité liée à la détaxe."
                })}
              </p>
              <div className={X.clsx(X.desktopOnly("block"))}>
                <X.Button
                  secondary
                  desktopLg
                  href="/blog"
                  label={tr({
                    en: "Explore all our articles",
                    fr: "Découvrez tous nos articles",
                  })}
                />
              </div>
            </div>
            <X.Image
              src={props.variant === "support" ? AltBlogImage : BlogImage}
              alt="Blog"
              className={X.clsx("w-[147px] xl:w-[200px] h-auto")}
            />
          </div>

          <div className={X.clsx(X.mobileOnly(), "mt-2 xl:mt-10")}>
            <X.Button
              secondary
              href="/blog"
              label={tr({
                en: "Explore all our articles",
                fr: "Découvrez tous nos articles",
              })}
            />
          </div>
        </div>

        <div className="flex-1 bg-yellow xl:rounded-4xl py-10 pb-8 xl:py-10 xl:pt-8 px-mobile-margin xl:px-10">
          <h3 className="text-[30px] xl:text-[48px] [&_strong]:font-medium w-[95%] xl:w-full leading-tight mb-8">
            {tr({
              en: <>Stay in the <strong>loop</strong></>,
              fr: <>Suivez nos dernières <strong>actualités</strong></>,
            })}
          </h3>
          <p className="text-[14px] xl:text-[18px] mb-4 font-medium xl:font-normal [&_strong]:font-bold leading-tight">
            {tr({
              en: <>Traveling, shopping, VAT refunds: get the best tips and deals straight to your inbox. <strong>Subscribe to our newsletter!</strong></>,
              fr: <>Voyages, shopping, détaxe : tous les bons plans dans votre boite mail ! <strong>Inscrivez-vous à notre newsletter</strong></>,
            })}
          </p>
          {form.isSent() ? (
            <div className="bg-white shadow-sm rounded-xl p-8 flex flex-col flex-1 justify-center items-center gap-4">
              <X.Image src={SuccessIcon} alt={tr({ en: "Success", fr: "Succès" })} className="w-[38px] h-auto mb-2" />
              <div className="text-[20px] font-bold text-center">{tr({ en: "Thank you!", fr: "Merci !" })}</div>
              <div className="text-[14px] font-medium text-center">{tr({
                en: "You are successfully subscribed to our newsletter",
                fr: "Nous avons bien enregistré votre inscription à notre newsletter",
              })}</div>
            </div>

          ) : (
            <form className="flex flex-col gap-3 items-stretch" onSubmit={form.handleSubmit}>
              <div className="flex flex-col items-stretch">
                <div className="relative flex flex-col items-stretch">
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={tr({ en: "Your e-mail address", fr: "Votre e-mail" })}
                    className={X.clsx(
                      "relative text-[16px] px-4 py-4 rounded-xl border bg-white placeholder:text-[12px]",
                      form.hasTouchedError() ? "border-danger" : "border-white",
                    )}
                    onBlur={form.handleBlur}
                    onChange={form.handleChangeEmail}
                    value={form.getEmail()}
                    required
                  />
                  {form.hasTouchedError() && (
                    <X.Image src={WarningIcon} alt={tr({ en: "Error", fr: "Erreur" })} className="w-[24px] h-auto absolute right-4 top-1/2 -translate-y-1/2" />
                  )}
                </div>
                {form.hasTouchedError() && (
                  <p className="text-danger text-[12px] mt-1 leading-tight">
                    {tr({
                      en: "Please enter a valid email address",
                      fr: "Veuillez introduire un e-mail valide",
                    })}
                  </p>
                )}
              </div>
              <p className="text-[10px] xl:text-[12px] mb-4 xl:mb-2 mt-2 xl:mt-0 leading-tight">
                {tr({
                  en: "By sharing your email, you agree to receive updates from Zapptax and confirm that you’ve read our privacy policy.",
                  fr: "En communiquant mon adresse e-mail, j'accepte de recevoir des informations de la part de Zapptax et je reconnais avoir pris connaissance de la politique de confidentialité.",
                })}
              </p>
              <div className="flex flex-row justify-start">
                <X.Button
                  secondary lg
                  disabled={!form.canSubmit()}
                  processing={form.isSubmitting()}
                  label={tr({ en: "Subscribe", fr: "Je m'inscris" })}
                  submit
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
